import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RocketLaunch } from "@phosphor-icons/react";
import api from "@/lib/api";
import { BTN_BLACK, BTN_WHITE, errText } from "@/components/pageSettings/AdminModal";
import { GoLiveSection } from "./GoLiveSection";
import { useIsPhone } from "@/lib/useIsPhone";

const HIDE_KEY = "golive-checklist-hidden";

function Header({ data, hidden, busy, onHide, onRefresh, onTickPassing }) {
  const pct = data ? Math.round((100 * data.done) / Math.max(data.total, 1)) : 0;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <RocketLaunch size={22} weight="bold" />
      <div className="mr-auto">
        <div className="label-mono">Owner checklist</div>
        <div className="font-chivo text-xl font-black leading-tight">Ready to go live?</div>
      </div>
      {data && (
        <span className={`px-2 py-1 text-[11px] uppercase tracking-widest ${data.ready ? "bg-emerald-600 text-white" : "border border-black"}`}
          data-testid="golive-status">
          {data.ready ? "Ready to go live" : `${data.done} of ${data.total} done`}
        </span>
      )}
      {!hidden && (
        <>
          <button type="button" className={BTN_BLACK} disabled={busy || !data} onClick={onTickPassing} data-testid="golive-tick-passing">Tick all passing checks</button>
          <button type="button" className={BTN_WHITE} disabled={busy} onClick={onRefresh} data-testid="golive-refresh">Re-check</button>
        </>
      )}
      <button type="button" className={BTN_WHITE} onClick={onHide} data-testid="golive-toggle">{hidden ? "Show" : "Hide"}</button>
      <div className="h-2 w-full border border-black bg-white" data-testid="golive-progress">
        <div className="h-full bg-emerald-600 transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export function GoLiveChecklist() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(() => localStorage.getItem(HIDE_KEY) === "1");
  const phone = useIsPhone();
  const run = useCallback(async (req, okMsg) => {
    setBusy(true);
    try {
      const r = await req();
      setData(r.data);
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(errText(e, "Couldn't update the checklist"));
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { run(() => api.get("/admin/go-live-checklist")); }, [run]);
  const toggleHidden = () => { localStorage.setItem(HIDE_KEY, hidden ? "0" : "1"); setHidden(!hidden); };
  const toggle = (key, done) => run(() => api.put(`/admin/go-live-checklist/items/${key}`, { done }));
  const isDone = (s) => s.items.every((i) => i.done);
  const sections = data && phone ? [...data.sections.filter((s) => !isDone(s)), ...data.sections.filter(isDone)] : data?.sections;
  return (
    <section className="mb-6 border-2 border-black bg-white p-4 shadow-[4px_4px_0_0_#000]" data-testid="golive-checklist">
      <Header data={data} hidden={hidden} busy={busy} onHide={toggleHidden}
        onRefresh={() => run(() => api.get("/admin/go-live-checklist"), "Checks refreshed")}
        onTickPassing={() => run(() => api.post("/admin/go-live-checklist/tick-passing"), "Passing checks ticked")} />
      {!hidden && data && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          {sections.map((s) => <GoLiveSection key={s.key} section={s} busy={busy} onToggle={toggle}
            onRefresh={() => run(() => api.get("/admin/go-live-checklist"))} />)}
        </div>
      )}
    </section>
  );
}
